import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectService } from '../services/projectService';
import { authService } from '../services/authService';
import { useLoading } from '../contexts/LoadingContext';
import { useChat } from '../contexts/ChatContext';
import LogoPopup from './LogoPopup';
import IOSAppDialog from './IOSAppDialog';
import ConfirmDialog from './ConfirmDialog';
import RenameDialog from './RenameDialog';
import AdminBadge from './AdminBadge';
import { useAdmin } from '../hooks/useAdmin';
import NotificationBubble from './NotificationBubble';
import {
  Badge,
  Button,
  Card,
  Heading,
  IconButton,
  PageContainer,
  Text,
} from './ui';
import { uploadAndExtractFloorplan } from '../services/floorplanExtractionService';
import { getProjectCoverPhotoUrl } from '../utils/projectCoverPhoto';
import DormWizardModal from './DormWizardModal';
import ChooseFloorPlanModal from './ChooseFloorPlanModal';
import { designPagePath } from '../books/jobPageModel';
import '../styles/UserHome.css';

const HouseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9 22V12H15V22"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Renders the project card's leading visual: cover photo from the gallery
// (resolved via getProjectCoverPhotoUrl), or the house-shape placeholder
// SVG when the project has no photos. See utils/projectCoverPhoto.js for
// selection logic.
function ProjectThumbnail({ project }) {
  const coverUrl = getProjectCoverPhotoUrl(project);
  if (coverUrl) {
    return (
      <div className="project-icon project-icon-photo">
        <img
          src={coverUrl}
          alt={`${project.name || 'Project'} cover`}
          loading="lazy"
          className="project-cover-image"
        />
      </div>
    );
  }
  return (
    <div className="project-icon">
      <HouseIcon />
    </div>
  );
}

function UserHome() {
  const [projects, setProjects] = useState([]);
  const [sharedProjects, setSharedProjects] = useState([]);
  // The user's organization's projects that someone else owns
  // (projectService.getAccessibleProjects), each with its access level.
  const [teamProjects, setTeamProjects] = useState([]);
  // A field member's team list is their assignments, and they create no
  // projects (see canCreateProjects / seesOnlyAssignedProjects).
  const [assignedOnly, setAssignedOnly] = useState(false);
  const [canCreate, setCanCreate] = useState(false);
  const [loading, setLoadingState] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [showIOSDialog, setShowIOSDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    projectId: null,
    projectName: null,
    isEmpty: true,
    isCheckingEmpty: false,
  });
  const [renameDialog, setRenameDialog] = useState({
    isOpen: false,
    projectId: null,
    projectName: null,
  });
  const [menuOpenProjectId, setMenuOpenProjectId] = useState(null);
  const [floorplanStatus, setFloorplanStatus] = useState(null);
  const [showDormWizard, setShowDormWizard] = useState(false);
  // A DXF can hold several floor plans; the extractor asks which one.
  const [planChoice, setPlanChoice] = useState(null);
  const floorplanInputRef = useRef(null);
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const { showLoading, hideLoading } = useLoading();
  const { isAdmin, isStudent, loading: adminLoading } = useAdmin();
  const { updateContext } = useChat();

  // Handle floor plan upload: create project + extract structure
  const handleFloorplanUpload = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file || !user) return;
      e.target.value = '';
      try {
        setFloorplanStatus('Creating project...');
        const projectRef = await projectService.createProject({
          name: file.name.replace(/\.[^.]+$/, ''),
        });

        setFloorplanStatus('Processing floor plan...');
        const result = await uploadAndExtractFloorplan(
          file,
          projectRef.id,
          (_stage, msg) => setFloorplanStatus(msg),
          {
            // Resolved by the modal below. A DXF with several plans must
            // not silently import the first and drop the other storeys.
            onChoosePlan: (plans) =>
              new Promise((resolve) => setPlanChoice({ plans, resolve })),
          }
        );
        setFloorplanStatus(null);
        navigate(`/structure/${result.structureId}`);
      } catch (err) {
        console.error('Floor plan upload failed:', err);
        setFloorplanStatus(null);
        setNotification({
          message: `Upload failed: ${err.message}`,
          type: 'error',
        });
      }
    },
    [user, navigate]
  );

  useEffect(() => {
    if (!adminLoading) {
      loadProjects();

      // Set user info in context
      if (user) {
        updateContext({
          user: {
            id: user.uid,
            isAdmin: isAdmin || false,
          },
        });
      }
    }
  }, [adminLoading, isAdmin]);

  const loadProjects = async () => {
    try {
      setLoadingState(true);
      showLoading();

      // Always get owned, shared and team projects for the current user
      const {
        ownedProjects,
        sharedProjects,
        teamProjects,
        assignedOnly,
        canCreateProjects,
      } = await projectService.getAccessibleProjects();
      setProjects(ownedProjects);
      setSharedProjects(sharedProjects);
      setTeamProjects(teamProjects);
      setAssignedOnly(assignedOnly);
      setCanCreate(canCreateProjects);

      // The admin "All Other Projects" section is gone, and with it the fetch
      // that read EVERY project in the database on an admin's home page just to
      // populate it.
      //
      // That fetch also fed the chat context cache — but only on the
      // cloud-functions branch. The dev branch never assigned
      // `allProjectsForCache`, so admins on dev, and every non-admin
      // everywhere, already got an empty projects cache. It is now built from
      // the projects the user can actually reach, which is consistent for
      // everyone and no longer costs an all-projects read.
      const allProjectsForCache = [
        ...ownedProjects,
        ...sharedProjects,
        ...teamProjects,
      ];

      // Update chat context with projects cache
      updateContext({
        cache: {
          projects: allProjectsForCache.map((p) => ({
            id: p.id,
            name: p.name,
            structureCount: p.structureCount || 0,
          })),
          structures: [], // Clear structures when on home page
          rooms: [], // Clear rooms
          checkpoints: [], // Clear checkpoints
        },
        location: {
          projectId: null,
          projectName: null,
          structureId: null,
          structureName: null,
          roomId: null,
          roomName: null,
          path: window.location.pathname,
        },
      });
    } catch (err) {
      console.error('Error loading projects:', err);
      setError('Failed to load projects. Please try again later.');
    } finally {
      setLoadingState(false);
      hideLoading();
    }
  };

  const handleProjectClick = (project, event) => {
    // Don't navigate if clicking on menu button
    if (
      event.target.closest('.project-menu-button') ||
      event.target.closest('.project-menu-dropdown')
    ) {
      return;
    }
    // the design project, never a contractor's job: this is the list of scans
    navigate(designPagePath(project.id));
  };

  const handleMenuToggle = (projectId, event) => {
    event.stopPropagation();
    setMenuOpenProjectId(menuOpenProjectId === projectId ? null : projectId);
  };

  const checkProjectEmpty = async (projectId) => {
    try {
      // Check for structures
      const { getStructuresByProjectId } = await import(
        '../services/structureService'
      );
      const structures = await getStructuresByProjectId(projectId);

      if (structures && structures.length > 0) {
        return false;
      }

      // TODO: Add checks for other linked resources (documents, renderings, etc)
      // For now, we only check structures

      return true;
    } catch (error) {
      console.error('Error checking if project is empty:', error);
      return false;
    }
  };

  const handleDeleteClick = async (project, event) => {
    event.stopPropagation();
    setMenuOpenProjectId(null);

    // Show checking dialog
    setDeleteDialog({
      isOpen: true,
      projectId: project.id,
      projectName: project.name,
      isEmpty: true,
      isCheckingEmpty: true,
    });

    // Check if project is empty
    const isEmpty = await checkProjectEmpty(project.id);

    // Update dialog with result
    setDeleteDialog({
      isOpen: true,
      projectId: project.id,
      projectName: project.name,
      isEmpty: isEmpty,
      isCheckingEmpty: false,
    });
  };

  const handleConfirmDelete = async () => {
    const { projectId } = deleteDialog;

    try {
      showLoading();
      await projectService.deleteProject(projectId);

      // Refresh projects list
      await loadProjects();

      setDeleteDialog({
        isOpen: false,
        projectId: null,
        projectName: null,
        isEmpty: true,
        isCheckingEmpty: false,
      });
    } catch (error) {
      console.error('Error deleting project:', error);
      setNotification({
        message: 'Failed to delete project. Please try again.',
        type: 'error',
      });
    } finally {
      hideLoading();
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialog({
      isOpen: false,
      projectId: null,
      projectName: null,
      isEmpty: true,
      isCheckingEmpty: false,
    });
  };

  const handleRenameClick = (project, event) => {
    event.stopPropagation();
    setMenuOpenProjectId(null);
    setRenameDialog({
      isOpen: true,
      projectId: project.id,
      projectName: project.name,
    });
  };

  const handleConfirmRename = async (newName) => {
    const { projectId } = renameDialog;

    try {
      showLoading();
      await projectService.updateProject(projectId, { name: newName });

      // Refresh projects list
      await loadProjects();

      setRenameDialog({
        isOpen: false,
        projectId: null,
        projectName: null,
      });
    } catch (error) {
      console.error('Error renaming project:', error);
      setNotification({
        message: 'Failed to rename project. Please try again.',
        type: 'error',
      });
    } finally {
      hideLoading();
    }
  };

  const handleCancelRename = () => {
    setRenameDialog({
      isOpen: false,
      projectId: null,
      projectName: null,
    });
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest('.project-menu-button') &&
        !event.target.closest('.project-menu-dropdown')
      ) {
        setMenuOpenProjectId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await authService.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getUserFirstName = () => {
    if (!user?.email) return 'User';
    const name = user.email.split('@')[0];
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';

    let date;
    try {
      // Handle Firestore Timestamp objects
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      }
      // Handle Firestore timestamp with seconds
      else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      }
      // Handle _seconds format (from Firebase Admin SDK)
      else if (timestamp._seconds) {
        date = new Date(timestamp._seconds * 1000);
      }
      // Handle regular date strings or Date objects
      else {
        date = new Date(timestamp);
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', timestamp);
        return 'Invalid Date';
      }

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'Invalid Date';
    }
  };

  if (loading) {
    return null; // LoadingProvider will show the loading screen
  }

  // "Add New Project" (+ admin-only Upload Floor Plan, + Make Dorm for admins
  // and students) cards, rendered at the end of the project list AND in the
  // empty state.
  const creationCards = (
    <>
      {/* Add New Project Box */}
      <Card
        variant="glass"
        interactive
        padding="lg"
        className="project-item new-project-item"
        onClick={() => setShowIOSDialog(true)}
      >
        <div className="project-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M12 5V19M5 12H19"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className="project-content">
          <h3 className="project-name">Add New Project</h3>
          <span className="project-date">Use iOS app to create</span>
        </div>
      </Card>

      {planChoice && (
        <ChooseFloorPlanModal
          plans={planChoice.plans}
          onChoose={(index) => {
            planChoice.resolve(index);
            setPlanChoice(null);
          }}
          onCancel={() => {
            planChoice.resolve(null);
            setPlanChoice(null);
          }}
        />
      )}

      {/* Upload Floor Plan - admin only */}
      {isAdmin && (
        <Card
          variant="glass"
          interactive
          padding="lg"
          className="project-item new-project-item"
          onClick={() => floorplanInputRef.current?.click()}
        >
          <div className="project-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M4 16L4 17C4 18.6569 5.34315 20 7 20L17 20C18.6569 20 20 18.6569 20 17L20 16M16 8L12 4M12 4L8 8M12 4L12 16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="project-content">
            <h3 className="project-name">Upload Floor Plan</h3>
            <span className="project-date">
              {floorplanStatus || 'Create from image or DXF'}
            </span>
          </div>
          <input
            ref={floorplanInputRef}
            type="file"
            accept="image/*,.dxf"
            style={{ display: 'none' }}
            onChange={handleFloorplanUpload}
          />
        </Card>
      )}

      {/* Make Dorm — admins, and students (an account that redeemed a student
        preview code; `accountKind` is server-stamped by redeemPreviewCode, so
        a client cannot write it). iOS gates its Dorm Room card on the same two,
        via `DormChooserAccess` — nobody else has a dorm path on either
        surface. */}
      {(isAdmin || isStudent) && (
        <Card
          variant="glass"
          interactive
          padding="lg"
          className="project-item new-project-item"
          onClick={() => setShowDormWizard(true)}
        >
          <div className="project-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <rect
                x="3"
                y="8"
                width="18"
                height="13"
                rx="1"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path d="M3 17H21" stroke="currentColor" strokeWidth="2" />
              <path
                d="M7 17V12H11V17"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14 14H18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M14 12H18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <div className="project-content">
            <h3 className="project-name">Make Dorm</h3>
            <span className="project-date">Create dorm room layout</span>
          </div>
        </Card>
      )}
    </>
  );

  return (
    <div className="user-home-container">
      {notification && (
        <NotificationBubble
          {...notification}
          onClose={() => setNotification(null)}
        />
      )}
      <header className="user-home-header">
        <div className="user-home-header-content">
          <div className="user-home-header-left">
            <LogoPopup
              onLogout={handleLogout}
              // One way in, not two: the Price Book lives inside Account
              // now, and a second path to the same page from the same
              // header is how the two drift apart.
              customActions={[
                {
                  label: 'Account',
                  onClick: () => navigate('/profile'),
                },
              ]}
            />
            <span className="welcome-text">Welcome {getUserFirstName()}!</span>
            <AdminBadge style={{ marginLeft: '12px' }} />
          </div>
          {/* The visible route to a user's own settings — the logo popup
              hid it, which is why the Price Book went unfound. */}
          <div className="user-home-header-right">
            <Button
              type="button"
              tone="dark"
              variant="secondary"
              onClick={() => navigate('/profile')}
            >
              Account
            </Button>
          </div>
        </div>
      </header>

      <main className="user-home-main">
        <PageContainer maxWidth="fluid">
          <div className="projects-section">
            <div className="user-home-section-header">
              <Heading level={3} tone="dark">
                Projects
              </Heading>
            </div>

            {error && <div className="error-message">{error}</div>}

            {projects.length === 0 ? (
              <>
                <div className="user-home-empty-state">
                  <Text tone="dark" size="md">
                    No projects yet
                  </Text>
                  <Text tone="dark" variant="muted">
                    Scan a space with the iOS app to create your first project.
                  </Text>
                </div>
                {/* Same creation cards as the populated list, so a new user
                    has something to click instead of a dead end. */}
                {canCreate && (
                  <div className="projects-list" data-testid="project-list">
                    {creationCards}
                  </div>
                )}
              </>
            ) : (
              <div className="projects-list" data-testid="project-list">
                {projects.map((project) => (
                  <Card
                    key={project.id}
                    variant="glass"
                    interactive
                    padding="lg"
                    className="project-item"
                    data-testid={`project-card-${project.id}`}
                    onClick={(e) => handleProjectClick(project, e)}
                  >
                    <div className="project-menu-container">
                      <IconButton
                        aria-label="Project options"
                        tone="dark"
                        variant="ghost"
                        shape="circle"
                        size="sm"
                        className="project-menu-button"
                        onClick={(e) => handleMenuToggle(project.id, e)}
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle cx="12" cy="5" r="1.5" fill="currentColor" />
                          <circle cx="12" cy="12" r="1.5" fill="currentColor" />
                          <circle cx="12" cy="19" r="1.5" fill="currentColor" />
                        </svg>
                      </IconButton>
                      {menuOpenProjectId === project.id && (
                        <div className="project-menu-dropdown">
                          <button
                            type="button"
                            className="menu-item"
                            onClick={(e) => handleRenameClick(project, e)}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            Rename
                          </button>
                          <button
                            type="button"
                            className="menu-item delete-item"
                            onClick={(e) => handleDeleteClick(project, e)}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                    <ProjectThumbnail project={project} />
                    <div className="project-content">
                      <h3 className="project-name">{project.name}</h3>
                      <span className="project-date">
                        {formatDate(project.createdAt)}
                      </span>
                    </div>
                  </Card>
                ))}

                {canCreate && creationCards}
              </div>
            )}
          </div>

          {/* Team Projects: the organization's projects someone else owns.
              A member's role is the same on every one of them, so the
              read-only badge is the section's, not each card's. */}
          {/* A field member always sees this section: their assignments are
              the reason they are here, and "none yet" is worth saying. */}
          {assignedOnly && teamProjects.length === 0 && (
            <div className="projects-section">
              <div className="user-home-section-header">
                <Heading level={3} tone="dark">
                  Assigned to you
                </Heading>
              </div>
              <div className="user-home-empty-state">
                <Text tone="dark" size="md">
                  No properties assigned yet
                </Text>
                <Text tone="dark" variant="muted">
                  Your office assigns you the properties to scan.
                </Text>
              </div>
            </div>
          )}

          {teamProjects.length > 0 && (
            <div className="projects-section">
              <div className="user-home-section-header">
                <Heading level={3} tone="dark">
                  {assignedOnly ? 'Assigned to you' : 'Team Projects'}
                </Heading>
                {teamProjects.every((p) => p.accessLevel === 'viewer') && (
                  <Badge variant="default" tone="dark" pill>
                    View Only
                  </Badge>
                )}
              </div>
              <div className="projects-list">
                {teamProjects.map((project) => (
                  <Card
                    key={project.id}
                    variant="glass"
                    interactive
                    padding="lg"
                    className="project-item"
                    onClick={(e) => handleProjectClick(project, e)}
                  >
                    <ProjectThumbnail project={project} />
                    <div className="project-content">
                      <h3 className="project-name">{project.name}</h3>
                      <span className="project-date">
                        {formatDate(project.createdAt)}
                      </span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Shared Projects Section */}
          {sharedProjects.length > 0 && (
            <div className="projects-section shared-section">
              <div className="user-home-section-header">
                <Heading level={3} tone="dark">
                  Shared With You
                </Heading>
                <Badge variant="default" tone="dark" pill>
                  View Only
                </Badge>
              </div>
              <div className="shared-section-notice">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ marginRight: '8px' }}
                  aria-hidden="true"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                These projects have been shared with you for viewing. You can
                make a copy to edit.
              </div>
              <div className="projects-list">
                {sharedProjects.map((project) => (
                  <Card
                    key={project.id}
                    variant="glass"
                    interactive
                    padding="lg"
                    className="project-item shared-project"
                    onClick={(e) => handleProjectClick(project, e)}
                  >
                    <div className="shared-indicator">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </div>
                    <ProjectThumbnail project={project} />
                    <div className="project-content">
                      <h3 className="project-name">{project.name}</h3>
                      <span className="project-date">
                        {formatDate(project.createdAt)}
                      </span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </PageContainer>
      </main>

      <IOSAppDialog
        isOpen={showIOSDialog}
        onClose={() => setShowIOSDialog(false)}
        type="project"
      />

      <RenameDialog
        isOpen={renameDialog.isOpen}
        onConfirm={handleConfirmRename}
        onCancel={handleCancelRename}
        title="Rename Project"
        currentName={renameDialog.projectName}
        placeholder="Enter new project name"
      />

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onConfirm={
          deleteDialog.isEmpty && !deleteDialog.isCheckingEmpty
            ? handleConfirmDelete
            : handleCancelDelete
        }
        onCancel={handleCancelDelete}
        title={
          deleteDialog.isCheckingEmpty
            ? 'Checking Project...'
            : deleteDialog.isEmpty
              ? 'Delete Project'
              : 'Cannot Delete Project'
        }
        message={
          deleteDialog.isCheckingEmpty
            ? 'Checking if project can be deleted...'
            : deleteDialog.isEmpty
              ? `Are you sure you want to delete "${deleteDialog.projectName}"? This action cannot be undone.`
              : `Cannot delete "${deleteDialog.projectName}" because it contains structures or other resources. Please delete all associated data first.`
        }
        confirmText={
          deleteDialog.isEmpty && !deleteDialog.isCheckingEmpty
            ? 'Delete'
            : 'OK'
        }
        cancelText={
          deleteDialog.isEmpty && !deleteDialog.isCheckingEmpty ? 'Cancel' : ''
        }
        confirmButtonStyle={
          deleteDialog.isEmpty && !deleteDialog.isCheckingEmpty
            ? 'danger'
            : 'primary'
        }
      />

      {isAdmin && (
        <DormWizardModal
          isOpen={showDormWizard}
          onClose={() => setShowDormWizard(false)}
        />
      )}
    </div>
  );
}

export default UserHome;
